import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, Checkbox, Col, DatePicker, Input, Radio, RadioChangeEvent, Row, Select, Space } from 'antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import { ScheduleSearchQuery } from './search_param';
import styles from './index.module.scss';
import { cssList } from '../../../utils/csslist';
import {
  DataType,
  SEARCH_EMPTY_SELECT,
  SectionDisplay,
  SectionDisplayKeys,
  TextType,
  Count,
  TimeSlot,
  DataTypeKeys, getEnumByKey, TextTypeKeys,
} from '../../../constant/index';
import { SectionIcon } from '../../components/section_icon/section_icon';
import { useSessionStore } from '../../../context/session';
import { useDivisionSelector } from '../../components/division_selector/interface';
import { DivisionProviderData } from '../../components/division_selector/provider';

type Props = {
  onSearch: (param: ScheduleSearchQuery) => void;
};


export const ScheduleSearchPanel: React.FC<Props> = ({ onSearch }) => {
  // const [data, setData] = useState<ScheduleSearchQuery>();
  const [height, setHeight] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [isSearchBox, setIsSearchBox] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const select = new URLSearchParams(location.search);// getパラメータを取得
  const [isTimePicker, setIsTimePicker] = useState(false);
  const [isSectionRadio, setIsSectionsRadio] = useState(true); // 検索区分ラジオボタンの活性状態
  // 項目=指定なしの状態管理
  const [isTextTypeNone, setIsTextTypeNone] = useState(true);

  // 街区・フロア、区画それぞれの値に連動した選択ボックスを作成する。
  const { Parts: { DivisionsProvider, Division, Floor, Block } } = useDivisionSelector();
  // useDivisionSelector にはPartsのそれぞれを組み合わせたDivisionSelectorという変数も作成してるので基本はそれを使う、でOKです。ここでは個別で使う例として上記で呼び出してます。
  // DivisionSelector を利用する場合はこんな感じのコードになります。
  //
  // const { DivisionSelector } = useDivisionSelector();
  //
  // 利用する箇所で以下のように設定
  // <DivisionSelector defaultDivisionId={divisionId} defaultFloorId={floorId} defaultBlockId={blockId} onChange={handleChangeDivisions} />

  const {
    selectedDivisionId,
  } = useSessionStore((selector) => selector);

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.scrollHeight);
      setOriginalHeight(ref.current.scrollHeight);
    }
  }, [onSearch]);


  const handleShowSearchButton = () => {
    setIsSearchBox(!isSearchBox);
    setHeight(isSearchBox ? 40 : originalHeight);
  };

  // パラメータを設定
  const createScheduleSearchQuery = (params: URLSearchParams, defaultQuery: ScheduleSearchQuery = { type: '0', date: dayjs(), timeGroup: 0, count: 25 }) => {
    const tmpquery = { ...defaultQuery };
    if (params.get('type')) {
      tmpquery.type = params.get('type') ?? undefined;
    }
    if (params.get('sections')) {
      tmpquery.sections = params.get('sections')?.split(',') ?? [];
    }
    if (params.get('division')) {
      tmpquery.division = parseInt(params.get('division'), 10) ?? undefined;
      if (Number.isNaN(tmpquery.division)) {
        tmpquery.division = 0;
      }
    } else {
      tmpquery.division = selectedDivisionId;
    }
    if (params.get('floor')) {
      tmpquery.floor = parseInt(params.get('floor'), 10) ?? undefined;
      if (Number.isNaN(tmpquery.floor)) {
        tmpquery.floor = 0;
      }
    }
    if (params.get('block')) {
      tmpquery.block = parseInt(params.get('block'), 10) ?? undefined;
      if (Number.isNaN(tmpquery.floor)) {
        tmpquery.floor = 0;
      }
    }
    if (params.get('date')) {
      tmpquery.date = dayjs(params.get('date'), 'YYYY-MM-DD') ?? undefined;
    }
    if (params.get('start')) {
      tmpquery.start = dayjs(params.get('start'), 'HH:mm:ss') ?? undefined;
    }
    if (params.get('end')) {
      tmpquery.end = dayjs(params.get('end'), 'HH:mm:ss') ?? undefined;
    }
    if (params.get('timeSlot')) {
      tmpquery.timeSlot = getEnumByKey(TimeSlot, params.get('timeSlot') ?? '') ?? undefined;
    }
    if (params.get('textType')) {
      tmpquery.textType = params.get('textType') ?? undefined;
    }
    if (params.get('timeGroup')) {
      tmpquery.timeGroup = parseInt(params.get('timeGroup'), 10) ?? 0;
      if (Number.isNaN(tmpquery.timeGroup)) {
        tmpquery.timeGroup = 0;
      }
    }
    if (params.get('content')) {
      tmpquery.content = params.get('content') ?? undefined;
    }
    if (params.get('count')) {
      tmpquery.count = parseInt(params.get('count'), 10) ?? undefined;
    }
    return tmpquery;
  };

  // 検索項目の状態を管理
  const [query, setQuery] = useState<ScheduleSearchQuery>(createScheduleSearchQuery(select));

  /** 業務データ種別 */
  const dataTypeOptions = [
    { value: '0', label: SEARCH_EMPTY_SELECT },
    ...DataType.map((type) => {
      return { value: type.key, label: type.value };
    }),
  ];
  /** 検索区分（作業区分コード） */
  const sectionOptions = [
    ...SectionDisplay.map((section) => {
      return {
        value: section.key,
        label: (
          <span className={styles.sectionChk}>
            <SectionIcon sectionCode={section.key}
                         size={(section.key === SectionDisplayKeys.key ? 20 : 22)}/>
            <span className={styles.sectionChkText}>{section.value}</span>
          </span>
        ),
      };
    }),
  ];
  /** 部分一致の項目 */
  const textTypeOptions = [
    { value: TextTypeKeys.textTypeNone, label: SEARCH_EMPTY_SELECT },
    ...TextType.map((type) => {
      return { value: type.key, label: type.value };
    }),
  ];

  const [autoSearch, setAutoSearch] = useState(false);

  useEffect(() => {
    setIsSectionsRadio(query.type === DataTypeKeys.dataTypeBispot || query.type === '0'); // 業務データ種別=Bispot以外は、検索区分ラジオボタンを非活性
  }, [query.type]);

  // 検索項目の初期化
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setQuery((q) => {
      const tmpquery = { ...q };
      return createScheduleSearchQuery(params, tmpquery);
    });
    setAutoSearch(true);
  }, [location.search]);

  useEffect(() => {
    if (autoSearch) {
      // 初期表示時にスケジュール一覧を検索
      onSearch(query);
      setAutoSearch(false);
    }
  }, [autoSearch]);

  const style = useMemo(() => {
    if (height === 0) {
      return {};
    }
    return { height };
  }, [height]);

  // 検索ボタン押下時
  const handleClick = useCallback(() => {
    onSearch(query);
  }, [query]);

  // 街区プルダウン変更時
  const handleChangeDivisions = (data: DivisionProviderData) => {
    setQuery((query) => {
      return { ...query, division: data.divisionId, floor: data.floorId, block: data.blockId };
    });
  };
  /** 時間帯 */
  const handleChangeDate = (e: RadioChangeEvent) => {
    const type:number = e.target.value;
    setIsTimePicker(false);
    if (type === 0) {
      // 指定なし
      setQuery((q) => {
        return { ...q, timeGroup: 0, end: null, start: null, timeSlot: null };
      });
    } else if (type === 1) {
      // 期間指定
      setIsTimePicker(true);
      setQuery((q) => {
        return { ...q, timeGroup: 1, end: null, start: null, timeSlot: null };
      });
    } else if (type === 2) {
      // 午前
      setQuery((q) => {
        return { ...q, timeGroup: 2, end: null, start: null, timeSlot: TimeSlot.morning };
      });
    } else if (type === 3) {
      // 午後
      setQuery((q) => {
        return { ...q, timeGroup: 3, end: null, start: null, timeSlot: TimeSlot.afternoon };
      });
    } else if (type === 4) {
      // 夜間
      setQuery((q) => {
        return { ...q, timeGroup: 4, end: null, start: null, timeSlot: TimeSlot.night };
      });
    } else if (type === 5) {
      // 全日
      setQuery((q) => {
        return { ...q, timeGroup: 5, end: null, start: null, timeSlot: TimeSlot.all_day };
      });
    }
  };

  /** 項目プルダウン変更時 */
  const handleTextType = (value: string) => {
    setQuery((q) => {
      return { ...q, textType: value };
    });

    // テキスト欄を制御
    if (value === TextTypeKeys.textTypeNone) {
      setIsTextTypeNone(true);
    } else {
      setIsTextTypeNone(false);
    }
  };
  return (
    <div
      ref={ref}
      style={style}
      className={cssList(styles.section, styles.relative, styles.searchBox, {
        [styles.hide]: !isSearchBox,
      })}
    >
      <span className={styles.menuShowBtn}>
        <Button
          type={'dashed'}
          size={'small'}
          onClick={handleShowSearchButton}
          style={{ color: 'gray' }}
        >
          {isSearchBox ?? true ? '検索条件 非表示' : '検索条件 表示'}
        </Button>
      </span>
      <div>
        <Row className={styles.row}>
          <div className={styles.formLabel}>
            業務データ種別
          </div>
          <Col span={4}>
            <Select
              style={{ width: 120 }}
              value={query.type}
              options={dataTypeOptions}
              onChange={(value) => setQuery((q) => {
                if (value !== DataTypeKeys.dataTypeBispot && value !== '0') {
                  return { ...q, type: value, sections: [] };
                }
                return { ...q, type: value };
              })
              }
            />
          </Col>
        </Row>
        <Row className={styles.row}>
          <div className={styles.formLabel}>
            検索区分
          </div>
          <Col>
            <Checkbox.Group
              value={query.sections}
              disabled={!isSectionRadio}
              options={sectionOptions}
              onChange={(value) => setQuery((q) => {
                return { ...q, sections: value };
              })}
            />
          </Col>
        </Row>
        <Row className={styles.row}>
          <DivisionsProvider defaultDivisionId={query.division} defaultFloorId={query.floor ?? 0} useLocalStorageIds={true}
                             defaultBlockId={query.block} onChange={handleChangeDivisions}>

            <div className={styles.formLabel}>
              街区
            </div>
            <Col span={4}>
              <Division selectStyle={{ width: 120 }}/>
            </Col>
            <div className={styles.formLabel2}>
              階
            </div>
            <Col span={4}>
              <Floor selectStyle={{ width: 120 }}/>
            </Col>
            <div className={styles.formLabel2}>
              区画
            </div>
            <Col>
              <Block selectStyle={{ width: 120 }}/>
            </Col>
          </DivisionsProvider>
        </Row>
        <Row className={styles.row}>
          <div className={styles.formLabel}>
            日付
          </div>
          <Col>
            <DatePicker
                locale={locale}
                value={query.date}
                allowClear={false}
                onChange={(value) => setQuery((q) => {
                  return {
                    ...q,
                    date: value,
                  };
                })}
            />
          </Col>
        </Row>
        <Row>
          <div className={styles.formLabel}>
            時間帯
          </div>
          <Col>
            <Radio.Group value={query.timeGroup} onChange={handleChangeDate}>
              <Radio value={0}>指定なし</Radio>
              <Radio value={1}>
                時間指定&nbsp;&nbsp;&nbsp;&nbsp;
                <DatePicker.RangePicker picker="time" locale={locale} disabled={!isTimePicker} allowEmpty={!isTimePicker}
                                        onChange={(dates) => setQuery((q) => {
                                          return {
                                            ...q,
                                            start: dates[0],
                                            end: dates[1],
                                          };
                                        })}
                />
              </Radio>
              <Radio value={2}>午前</Radio>
              <Radio value={3}>午後</Radio>
              <Radio value={4}>夜間</Radio>
              <Radio value={5}>全日</Radio>
            </Radio.Group>
          </Col>
        </Row>
        <Row className={styles.row}>
          <div className={styles.formLabel}>
            項目
          </div>
          <Col >
            <Select
              style={{ width: 120 }}
              defaultValue="0"
              options={textTypeOptions}
              onChange={(value) => handleTextType(value)}
            />
            &nbsp;&nbsp;に&nbsp;&nbsp;
            <Input
              style={{ width: 180 }}
              disabled={isTextTypeNone}
              defaultValue=""
              onChange={(e) => setQuery((q) => {
                return { ...q, content: e.target.value };
              })}
            />
            &nbsp;&nbsp;を含む
          </Col>
        </Row>
        <Row className={styles.row}>
          <div className={styles.formLabel}>
            取得件数
          </div>
          <Col >
            <Select
              style={{ width: 120 }}
              defaultValue={25}
              options={Count}
              onChange={(value) => setQuery((q) => {
                return { ...q, count: value };
              })}
            />
          </Col>
        </Row>
        <Row className={styles.row}>
          <Space>
            <Button type="primary" onClick={handleClick}>
              検索
            </Button>
          </Space>
        </Row>
      </div>
    </div>
  );
};
